import {
  UTILITY_CONFIGURATION_SOURCE_ADMIN,
  UTILITY_CONFIGURATION_SOURCE_PARTNER,
} from 'client/constants/utilityConfigurationSource';

// FEAT-F43 — "Set By" column filter on Synced Listings.
//
// The filter options mirror the badge labels the column actually renders
// (SrUtilityConfigurationSourceMarker): Defaults, UP, Partner, PMS Sync. "Set By" is the
// name of the column, not a value it displays, so it is not an option.
//
// The column *renders* from `utilityConfigurationProvenance` but *filters* on a separate
// derived column, `utilityConfigurationSetByFilter`, which collapses the five raw sources
// into the same four buckets the badge does. See the expression comment in
// server/models/Address/getPartnerAddresses.js.

export const SET_BY_FILTER_FIELD_ID = 'utilityConfigurationSetByFilter';

// Bucket values, matching SrUtilityConfigurationSourceMarker's badge grouping:
//   defaults — no provenance, or a `default`-source configuration
//   admin    — badge "UP"
//   partner  — badge "Partner"
//   pmsSync  — badge "PMS Sync" (rentvine + rentManager collapse into one bucket, exactly
//              as the badge does; the specific PMS shows only in the tooltip)
export const SET_BY_FILTER_VALUE_DEFAULTS = 'defaults';
export const SET_BY_FILTER_VALUE_ADMIN = UTILITY_CONFIGURATION_SOURCE_ADMIN;
export const SET_BY_FILTER_VALUE_PARTNER = UTILITY_CONFIGURATION_SOURCE_PARTNER;
export const SET_BY_FILTER_VALUE_PMS_SYNC = 'pmsSync';

// Labels are taken from the badge rather than from
// getUtilityConfigurationSourceDisplayName, because the badge and the callout deliberately
// differ: the badge shows "UP" where the callout shows "Utility Profit", and the filter has
// to read like the column it sits above.
export const SET_BY_FILTER_OPTIONS = [
  { value: SET_BY_FILTER_VALUE_DEFAULTS, label: 'Defaults' },
  { value: SET_BY_FILTER_VALUE_ADMIN, label: 'UP' },
  { value: SET_BY_FILTER_VALUE_PMS_SYNC, label: 'PMS Sync' },
  { value: SET_BY_FILTER_VALUE_PARTNER, label: 'Partner' },
];

// What the analytics event reports as `selectedOption`. The categorical filter is
// multi-select, so several options can be active at once; 'cleared' covers removing it.
export const SET_BY_FILTER_OPTION_CLEARED = 'cleared';

/**
 * Derives the analytics `selectedOption` from the data table's applied filter map.
 *
 * @param  {Object} filter  dataTableViewSettings.filter, may be undefined.
 * @return {String} a comma-joined, stably-ordered list of selected values, or 'cleared'.
 */
export function getSetByFilterSelectedOption(filter) {
  const value = (filter || {})[SET_BY_FILTER_FIELD_ID];

  if (!Array.isArray(value) || value.length === 0) {
    return SET_BY_FILTER_OPTION_CLEARED;
  }

  // Sort so a multi-select is reported consistently regardless of the order the user
  // happened to click the options in — otherwise the same selection produces different
  // event values and the adoption counts split across them.
  return [...value].sort().join(',');
}
